<template>
  <div class="pdb-6">
    <!-- <div class="tit-h5 pdb-1">{{ t('ss_proceed_subscription', { plan: selectedPlan }) }}</div> -->
    <p class="dim">{{ t('subscription_start_notice', { plan: selectedPlan }) }}</p>
  </div>

  <div class="plan-payment-info">
    <div class="info-box">
      <div class="inr-tit">
        <span>SconChat {{ selectedPlan }}</span>
        <span>{{
          t('period_payment', {
            period: isSelectedMonthly ? t('self_service_tab_monthly') : t('self_service_tab_yearly'),
          })
        }}</span>
      </div>
      <ul class="dt-info">
        <li>
          <span class="indent">{{ t('modal_subscribe_plan_price') }}</span>
          <span>{{ formattedPlanPrice }}</span>
        </li>
        <li>
          <span class="indent">{{ t('modal_subscribe_discount_amount') }}</span>
          <span>{{ formattedDiscountedPrice }}</span>
        </li>
      </ul>
    </div>
    <div class="total">
      <p>{{ t('total_payment') }}</p>
      <strong>{{ formattedFinalPrice }}</strong>
    </div>
  </div>

  <div class="modal-footer">
    <ButtonProcess @click="handlePayment" full-width :disabled="isLoading">
      {{ t('self_service_subscribe') }}
    </ButtonProcess>
  </div>
</template>

<script setup lang="ts">
import { camelCase, capitalize } from 'lodash';

import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import { useGtag } from '@/composables/shared/useGtag';
import { usePayment } from '@/composables/shared/usePayment';
import { usePlan } from '@/composables/shared/usePlan';
import { useSubscriptionStore } from '@/stores/subscription';
import { BillingCycleType, PlanPricing, TypePlan } from '@/types/components';

const props = withDefaults(
  defineProps<{
    payload: {
      plan: TypePlan;
      billingCycle: BillingCycleType;
      price: PlanPricing;
    };
  }>(),
  {}
);

const { t } = useI18n();

const subscriptionStore = useSubscriptionStore();

const { formatCurrency } = usePlan();
const { handleSubscriptionPayment } = usePayment();
const { trackEventFunnel } = useGtag();

const isLoading = ref<boolean>(false);

const selectedPlan = computed(() => capitalize(camelCase(props.payload?.plan)));
const isSelectedMonthly = computed(() => props.payload?.billingCycle === 'MONTHLY');
const currency = computed(() => subscriptionStore.currency);

const planPrice = computed(() => {
  const price = props.payload?.price[currency.value].pricePerMonth;
  return isSelectedMonthly.value ? price : price * 12;
});
const discountedPrice = computed(() =>
  isSelectedMonthly.value ? 0 : props.payload?.price[currency.value].annualDiscount
);
const finalPrice = computed(() => (planPrice.value ?? 0) - discountedPrice.value);

const formattedPlanPrice = computed(() => formatCurrency(planPrice.value, currency.value));
const formattedDiscountedPrice = computed(() => formatCurrency(discountedPrice.value, currency.value));
const formattedFinalPrice = computed(() => formatCurrency(finalPrice.value, currency.value));

const handlePayment = async () => {
  isLoading.value = true;

  const plan = props.payload?.plan;
  const billingCycle = props.payload?.billingCycle;

  trackEventFunnel('click_subscription_button', { plan, billingCycle });
  await handleSubscriptionPayment(plan, billingCycle, finalPrice.value);
};
</script>

<style scoped lang="scss">
::v-deep(.dynamic-table) {
  min-width: unset;
  margin: 0.5rem 0;
}

.alert-info {
  display: flex;
  gap: 0.5rem;
  background: var(--surface-primary-dimmest);
  padding: 0.5rem;
  border-radius: 8px;
  align-items: center;
  border: 1px solid var(--border-primary-dim);
}
.modal-footer {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.plan-payment-info {
  .info-box {
    background: $gray10;
    padding: 1.25rem;
    border-radius: $size-s;
    .inr-tit {
      display: flex;
      justify-content: space-between;
      align-items: center;
      span {
        font-size: $font-size-psmall;
        &:first-child {
          font-weight: 600;
        }
      }
    }
    .dt-info {
      padding: 0.5rem 0 0;
      li {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.125rem 0;
        span {
          font-size: $font-size-psmall;
          color: $gray90;
          &.indent {
            padding-left: 1rem;
            position: relative;
            &::before {
              content: '';
              position: absolute;
              left: 0;
              top: 1px;
              display: block;
              width: 1rem;
              height: 1rem;
              background: url('@/assets/images/common/icons/indent.svg') no-repeat center;
              opacity: 0.4;
            }
          }
        }
      }
    }
  }
  .total {
    display: flex;
    justify-content: space-between;
    padding: 1rem 0.5rem;
    p,
    strong {
      font-size: $font-size-h6;
    }
    strong {
      font-weight: 600;
    }
  }
}
</style>
